"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "Subscription gate",
    rule: "Creating a Branch requires the Branch feature to be active in your plan",
  },
  {
    topic: "Unique name",
    rule: "Branch names only need to be unique within the same Division, not across your whole company",
  },
  {
    topic: "Required on creation",
    rule: "Division, Branch Name, full Address (street, country, state, city, phone), and GSTIN are all mandatory. Pin Code and Branch Manager are the only optional fields",
  },
  {
    topic: "Branch Code",
    rule: "Pre-filled and locked in the form, but not actually guaranteed unique. The same code can and does appear on branches in different divisions",
  },
  {
    topic: "Branch Logo",
    rule: "The backend accepts a logo upload, but the current Add and Edit Branch forms have no file field to use it",
  },
  {
    topic: "Deactivating",
    rule: "Blocked while the branch has any open Quote, Sales Order, Delivery Challan, Invoice, Purchase Order, GRPO, Bill, Recurring Invoice, Recurring Expense, Recurring Bill, Production Order, Inventory Adjustment, Debit Note, or Credit Note",
  },
  {
    topic: "Deleting",
    rule: "Blocked outright if any Warehouse still exists under this branch, and also blocked if the branch was ever referenced by any document at all, even old and fully closed ones",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. A Branch belongs to one Division" },
      { heading: "2. Creating one asks for more than just a name" },
      { heading: "3. Deactivating checks for open documents, not history" },
      { heading: "4. Deleting is far stricter than deactivating" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function BranchPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Branch
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone setting up the branches that sit under each Division
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <span className="geist-medium text-[#111827]">Branch</span> is
            the middle level of the hierarchy, one step below Division and one
            above Warehouse. It carries its own address and GST registration,
            which is what makes it more than just a folder for organizing
            Warehouses.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Belongs to a Division */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. A Branch belongs to one Division
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            You pick the parent <span className="geist-medium text-[#111827]">Division</span>{" "}
            before anything else, and that choice changes what counts as a
            duplicate name.
          </p>
          <Definitions
            items={[
              {
                term: "Division",
                desc: "Required at creation. A Branch always belongs to exactly one Division.",
              },
              {
                term: "Branch Name",
                desc: "Only needs to be unique within that Division. The same name can exist in a different Division without conflict.",
              },
              {
                term: "Branch Code",
                desc: "Shown pre-filled and locked, the same way Division's code works, but it isn't actually enforced as unique. The real data on this company has BR1 repeated across three different divisions.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Branch list showing the same code repeated under different Division values." />

          {/* 2. Creating */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Creating one asks for more than just a name
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Unlike Division, a Branch&apos;s address and tax registration
            aren&apos;t optional extras.
          </p>
          <Definitions
            items={[
              {
                term: "Branch feature",
                desc: "Has to be active on your plan before Add Branch will let you save one.",
              },
              {
                term: "Address",
                desc: "Street, Country, State, City, and Phone are all required. Pin Code is the only optional part of the address.",
              },
              {
                term: "GSTIN",
                desc: "Required at creation. Each branch carries its own GST registration.",
              },
              {
                term: "Branch Manager",
                desc: "Name and phone are optional, unlike everything else on this form.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Branch form with Address and GSTIN marked required." />

          {/* 3. Deactivate */}
          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Deactivating checks for open documents, not history
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The same shape of check as Division, run against this branch instead.
          </p>
          <Definitions
            items={[
              {
                term: "Blocked while open",
                desc: "Any Quote, Sales Order, Delivery Challan, Invoice, Purchase Order, GRPO, Bill, active Recurring Invoice, Recurring Expense, or Recurring Bill, in-progress Production Order, draft Inventory Adjustment, or unclosed Debit Note or Credit Note stops the deactivation.",
              },
              {
                term: "Closed history doesn't count",
                desc: "A branch with nothing but paid, completed, or cancelled documents against it can be deactivated freely.",
              },
              {
                term: "Reactivating",
                desc: "Has no equivalent check. You can turn a branch back on any time.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Mark as Inactive action in a branch row's Actions menu." />

          {/* 4. Delete */}
          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Deleting is far stricter than deactivating
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <span className="geist-medium text-[#111827]">Delete</span> asks
            whether the branch was ever used at all, not just whether it&apos;s
            currently busy.
          </p>
          <Definitions
            items={[
              {
                term: "Children first",
                desc: "Blocked outright if any Warehouse still exists under this branch, regardless of that warehouse's own status.",
              },
              {
                term: "Any historical reference",
                desc: "Also blocked if the branch appears on any document at all, even one that's long since closed.",
              },
            ]}
          />
          <Callout type="warning">
            A branch that&apos;s actually been used for real business, even
            once, effectively can&apos;t be deleted. Deactivate is the
            realistic option once history exists.
          </Callout>
          <Screenshot caption="Screenshot: the delete confirmation blocked by an in-use branch." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <Link
            href="/documentation/books/all-master/division"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Division <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              The parent every Branch is created under
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/warehouse-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Warehouse Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Created under a Branch, one level below
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
